import { type FC } from "react"

import { toDateString } from "@forento/core/utilities/date"

import Button from "~/components/Button"
import Link from "~/components/Link"
import { DefaultModal, ModalButtons, ModalContentPadding, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

type Props = { membershipId: number | null; isOpen: boolean; onClose(): void }
const MembersModal: FC<Props> = ({ membershipId, isOpen, onClose }) => {
	const members = query.membership.listMembers.useQuery(membershipId ?? -1, {
		enabled: isOpen && membershipId !== null,
	})

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={1000}>
			<ModalTitle>Members</ModalTitle>
			<ModalContentPadding className="max-h-[60vh] overflow-y-auto">
				{members.error ? (
					<p>Failed to load members.</p>
				) : members.data === undefined ? (
					<PartialLoadingPage />
				) : members.data.length === 0 ? (
					<p className="text-light text-sm">This tier has no active members yet.</p>
				) : (
					<Table
						isCompact
						columns={[
							"Name",
							"Email",
							{ label: "Renewal", flex: { grow: 0, basis: 120 } },
							{ label: "Joined", flex: { grow: 0, basis: 80 } },
						]}
						data={members.data.map(member => [
							<Link to={routes.user.detail(member.id)}>
								{`${member.firstName} ${member.lastName}`.trim() || "(no name)"}
							</Link>,
							member.email,
							getRenewalLabel(member.subscription),
							toDateString(member.registerDate),
						])}
					/>
				)}
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose}>
					Close
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

function getRenewalLabel(subscription: {
	type: "stripe" | "paypal" | "external"
	paymentInterval?: "month" | "year"
	endDate: Date | null
}): string {
	if (subscription.endDate !== null) return `Ends ${toDateString(subscription.endDate)}`
	if (subscription.type === "external") return "Manual"
	return subscription.paymentInterval === "year" ? "Yearly" : "Monthly"
}

export default MembersModal
